// Document icon actions for the quote, invoice, and job cards.
//
// On a phone sheet these sit in the non-scrolling drawer header (LF-4).
// They used to live only in the module card, under contact actions, the
// stage hero, and the journey. A ~390×512 sheet paints the name and the
// close control and stops; the 44×44 buttons were in the DOM further down
// (and a non-wrapping row inside ModulePanel's overflow:hidden can clip
// them off the card). One cluster is mounted: the header on a sheet, the
// card on the side panel.

import { useState } from 'react'
import { Download, FileText, Pencil, Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { OfflineActions } from '@/components/ui/OfflineActions'
import { PdfPreviewSheet } from '@/components/PDF/PdfPreviewSheet'
import { apiClient } from '@/lib/api'
import { toast } from '@/lib/toast'
import type { Record_, Stage } from './types'

/** Which module answers "what is this record about right now". */
export function primaryModuleFor(stage: Stage): 'lead' | 'quote' | 'job' | 'invoice' {
  if (stage === 'LEAD' || stage === 'QUALIFIED') return 'lead'
  if (stage === 'QUOTED' || stage === 'ACCEPTED') return 'quote'
  if (stage === 'INVOICED' || stage === 'PAID') return 'invoice'
  return 'job'
}

export interface DocumentActionHandlers {
  onResendQuote?: (record: Record_) => void
  onEditQuote?: (record: Record_) => void
  onEditJob?: (record: Record_) => void
  onRescheduleJob?: (record: Record_) => void
  onResendInvoice?: (record: Record_) => void
  onRecordPayment?: (record: Record_) => void
  onEditInvoice?: (record: Record_) => void
  onSendReceipt?: (record: Record_) => void
}

export interface QuoteActionProps {
  record: Record_
  onResendQuote?: () => void
  onEditQuote?: () => void
}

export interface InvoiceActionProps {
  record: Record_
  onResendInvoice?: () => void
  onRecordPayment?: () => void
  onEditInvoice?: () => void
  onSendReceipt?: () => void
}

export interface JobActionProps {
  record: Record_
  onEditJob?: () => void
  onRescheduleJob?: () => void
}

export interface DocumentSlots {
  primary: 'lead' | 'quote' | 'job' | 'invoice'
  quote: QuoteActionProps | null
  invoice: InvoiceActionProps | null
  job: JobActionProps | null
}

const ICON = { width: 18, height: 18 }

/** Which document actions this record actually offers. Shared by the card and the sheet header. */
export function documentSlots(
  record: Record_,
  actions: DocumentActionHandlers,
  ctx: { showMoney: boolean; canEditJobs: boolean; canSendInvoice: boolean; canRecordPayment: boolean; canCreateInvoice: boolean },
): DocumentSlots {
  const hasQuote = ctx.showMoney && Boolean(record.lineItems && record.lineItems.length > 0)
  const quote: QuoteActionProps | null = hasQuote
    ? {
        record,
        onResendQuote: actions.onResendQuote ? () => actions.onResendQuote!(record) : undefined,
        onEditQuote: actions.onEditQuote ? () => actions.onEditQuote!(record) : undefined,
      }
    : null
  const jobStages: Stage[] = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'INVOICED', 'PAID']
  const job: JobActionProps | null = record.jobId || jobStages.includes(record.stage)
    ? {
        record,
        onEditJob: actions.onEditJob && ctx.canEditJobs ? () => actions.onEditJob!(record) : undefined,
        onRescheduleJob: actions.onRescheduleJob && ctx.canEditJobs ? () => actions.onRescheduleJob!(record) : undefined,
      }
    : null
  const invoice: InvoiceActionProps | null = ctx.showMoney && (record.stage === 'INVOICED' || record.stage === 'PAID')
    ? {
        record,
        onResendInvoice: actions.onResendInvoice && ctx.canSendInvoice ? () => actions.onResendInvoice!(record) : undefined,
        onRecordPayment: actions.onRecordPayment && ctx.canRecordPayment ? () => actions.onRecordPayment!(record) : undefined,
        onEditInvoice: actions.onEditInvoice && ctx.canCreateInvoice ? () => actions.onEditInvoice!(record) : undefined,
        onSendReceipt: actions.onSendReceipt && ctx.canSendInvoice ? () => actions.onSendReceipt!(record) : undefined,
      }
    : null
  return { primary: primaryModuleFor(record.stage), quote, invoice, job }
}

export function QuoteIconActions({ record, onResendQuote, onEditQuote }: QuoteActionProps) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const quoteId = record.quoteId
    ?? (record.id.startsWith('q:') ? record.id.slice(2) : null)

  const loadPdf = async (): Promise<Blob> => {
    const res = await apiClient.get(`/api/quotes/${quoteId}/pdf`, { responseType: 'blob' })
    return res.data as Blob
  }

  return (
    <>
      <div className="ds-drawer-icons">
        {onEditQuote && (
          <Button variant="icon" size="sm" aria-label="Edit quote" onClick={onEditQuote}>
            <Pencil style={ICON} />
          </Button>
        )}
        {record.stage === 'QUOTED' && onResendQuote && (
          <Button
            variant="icon"
            size="sm"
            aria-label={record.quoteSentAt ? 'Resend quote' : 'Send quote'}
            title={record.quoteSentAt ? 'Resend quote' : 'Send quote'}
            onClick={onResendQuote}
          >
            <Send style={ICON} />
          </Button>
        )}
        <Button
          variant="icon"
          size="sm"
          aria-label="Preview quote PDF"
          title="Preview quote PDF"
          onClick={() => setPreviewOpen(true)}
          disabled={!quoteId}
        >
          <FileText style={ICON} />
        </Button>
      </div>
      {quoteId && (
        <PdfPreviewSheet
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          title={`Quote ${record.quoteNumber ?? ''}`.trim()}
          fileName={record.quoteNumber ? `Quote-${record.quoteNumber}.pdf` : 'Quote.pdf'}
          load={loadPdf}
        />
      )}
    </>
  )
}

export function InvoiceIconActions({ record, onResendInvoice, onEditInvoice, onSendReceipt }: InvoiceActionProps) {
  const [downloading, setDownloading] = useState(false)
  const isPaid = record.stage === 'PAID'
  const invoiceId = record.invoiceId
    ?? (record.id.startsWith('inv:') ? record.id.slice(4) : null)

  const handleDownloadPdf = async () => {
    if (downloading) return
    if (!invoiceId) {
      toast.error("Couldn't download the invoice PDF. Refresh and try again.")
      return
    }
    setDownloading(true)
    try {
      const res = await apiClient.get(`/api/invoices/${invoiceId}/pdf`, { responseType: 'blob' })
      const blob = res.data as Blob
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = record.invoiceNumber ? `Invoice-${record.invoiceNumber}.pdf` : 'Invoice.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err: unknown) {
      toast.error(err, 'download the invoice PDF')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="ds-drawer-icons">
      {onEditInvoice && (
        <Button variant="icon" size="sm" aria-label="Edit invoice" onClick={onEditInvoice}>
          <Pencil style={ICON} />
        </Button>
      )}
      {!isPaid && onResendInvoice && (
        <Button variant="icon" size="sm" aria-label="Resend invoice" onClick={onResendInvoice}>
          <Send style={ICON} />
        </Button>
      )}
      {isPaid && onSendReceipt && (
        <Button variant="icon" size="sm" aria-label="Send receipt" onClick={onSendReceipt}>
          <Send style={ICON} />
        </Button>
      )}
      <Button
        variant="icon"
        size="sm"
        aria-label="Download invoice PDF"
        onClick={handleDownloadPdf}
        disabled={downloading || !invoiceId}
      >
        <Download style={ICON} />
      </Button>
    </div>
  )
}

export function JobIconActions({ onEditJob }: { onEditJob?: () => void }) {
  if (!onEditJob) return null
  return (
    <div className="ds-drawer-icons">
      <Button variant="icon" size="sm" aria-label="Edit job" onClick={onEditJob}>
        <Pencil style={ICON} />
      </Button>
    </div>
  )
}

/** Sheet-header cluster. Renders nothing when this record has no document icons. */
export function DrawerHeaderActions({ slots }: { slots: DocumentSlots }) {
  const order = [slots.primary, ...(['invoice', 'job', 'quote'] as const).filter((k) => k !== slots.primary)]
  const nodes = order.flatMap((key) => {
    if (key === 'quote' && slots.quote) return [<QuoteIconActions key="quote" {...slots.quote} />]
    if (key === 'invoice' && slots.invoice) return [<InvoiceIconActions key="invoice" {...slots.invoice} />]
    if (key === 'job' && slots.job?.onEditJob) return [<JobIconActions key="job" onEditJob={slots.job.onEditJob} />]
    return []
  })
  if (nodes.length === 0) return null
  return (
    <div
      data-testid="drawer-header-actions"
      role="group"
      aria-label="Document actions"
      className="ds-drawer-header-actions"
    >
      {/* Same offline gate the cards used: send, edit, and preview wait. */}
      <OfflineActions showReason={false}>{nodes}</OfflineActions>
    </div>
  )
}
